const PROCESSOR = 'voice-pcm-capture';

const WORKLET_SOURCE = `
class VoicePcmCapture extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const targetRate = options.processorOptions.targetRate;
    this.step = sampleRate / targetRate;
    this.size = Math.round(targetRate / 10);
    this.chunk = new Int16Array(this.size);
    this.filled = 0;
    this.position = 0;
    this.peak = 0;
    this.port.onmessage = () => this.emit(true);
  }
  emit(flushed) {
    const pcm = this.chunk.slice(0, this.filled).buffer;
    this.port.postMessage({ pcm, level: this.peak, flushed }, [pcm]);
    this.filled = 0;
    this.peak = 0;
  }
  process(inputs) {
    const input = inputs[0] && inputs[0][0];
    if (!input) return true;
    while (this.position < input.length) {
      const index = Math.floor(this.position);
      const next = index + 1 < input.length ? input[index + 1] : input[index];
      const value = input[index] + (next - input[index]) * (this.position - index);
      const sample = Math.max(-1, Math.min(1, value));
      this.peak = Math.max(this.peak, Math.abs(sample));
      this.chunk[this.filled++] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      if (this.filled === this.size) this.emit(false);
      this.position += this.step;
    }
    this.position -= input.length;
    return true;
  }
}
registerProcessor('${PROCESSOR}', VoicePcmCapture);
`;

export interface PcmCapture {
  flush(): Promise<void>;
  stop(): void;
}

interface PcmMessage {
  pcm: ArrayBuffer;
  level: number;
  flushed: boolean;
}

const FLUSH_TIMEOUT_MS = 500;

const isPcmMessage = (data: unknown): data is PcmMessage =>
  typeof data === 'object' &&
  data !== null &&
  'pcm' in data &&
  data.pcm instanceof ArrayBuffer &&
  'level' in data &&
  typeof data.level === 'number' &&
  'flushed' in data &&
  typeof data.flushed === 'boolean';

export async function startPcmCapture(input: {
  context: AudioContext;
  stream: MediaStream;
  sampleRate: number;
  onChunk: (pcm: ArrayBuffer) => void;
  onLevel: (level: number) => void;
}): Promise<PcmCapture> {
  const { context } = input;
  const moduleUrl = URL.createObjectURL(
    new Blob([WORKLET_SOURCE], { type: 'application/javascript' }),
  );
  try {
    await context.audioWorklet.addModule(moduleUrl);
  } finally {
    URL.revokeObjectURL(moduleUrl);
  }
  const source = context.createMediaStreamSource(input.stream);
  const node = new AudioWorkletNode(context, PROCESSOR, {
    processorOptions: { targetRate: input.sampleRate },
  });
  const flushWaiters: (() => void)[] = [];
  node.port.onmessage = (event: MessageEvent<unknown>) => {
    if (!isPcmMessage(event.data)) return;
    input.onLevel(event.data.level);
    if (event.data.pcm.byteLength > 0) input.onChunk(event.data.pcm);
    if (event.data.flushed) flushWaiters.splice(0).forEach((resolve) => resolve());
  };
  source.connect(node);
  node.connect(context.destination);
  return {
    flush: () =>
      new Promise<void>((resolve) => {
        flushWaiters.push(resolve);
        setTimeout(resolve, FLUSH_TIMEOUT_MS);
        node.port.postMessage('flush');
      }),
    stop: () => {
      node.port.onmessage = null;
      source.disconnect();
      node.disconnect();
    },
  };
}
